'use client';

import { Download, Share2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { CopyButton } from '@ui/components/common/copyButton';
import { Button } from '@ui/components/ui/button';
import { Skeleton } from '@ui/components/ui/skeleton';
import { generateProfileQRCode } from '@ui/utils/qrCode';

import { ShareOptionsDialog } from './ShareOptionsDialog';
import { ShareOptionsDrawer } from './ShareOptionsDrawer';

export interface ProfileShareContentProps {
  profileUrl: string;
  userName: string;
  logoUrl?: string;
  roleText?: string;
  companyName?: string;
  isMobile?: boolean;
  translations?: {
    shareDescription?: string;
    profileUrl?: string;
    saveAndDownload?: string;
    share?: string;
    shareVia?: string;
    copyLink?: string;
    shareViaWhatsApp?: string;
    shareViaFacebook?: string;
    shareViaX?: string;
    shareViaEmail?: string;
    moreOptions?: string;
    linkCopied?: string;
  };
  onSaveAndDownload?: (qrCodeDataUrl: string) => void;
  showHeader?: boolean;
}

export function ProfileShareContent({
  profileUrl,
  userName,
  logoUrl,
  roleText,
  companyName,
  isMobile = false,
  translations,
  onSaveAndDownload
}: ProfileShareContentProps) {
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(true);
  const [isShareMenuOpen, setIsShareMenuOpen] = useState(false);
  const copyButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const generateQR = async () => {
      try {
        setIsGenerating(true);
        const dataUrl = await generateProfileQRCode({
          userName,
          profileUrl,
          logoUrl,
          roleText,
          companyName
        });
        setQrCodeDataUrl(dataUrl);
      } catch (error) {
        console.error('Failed to generate QR code:', error);
      } finally {
        setIsGenerating(false);
      }
    };

    generateQR();
  }, [userName, profileUrl, logoUrl, roleText, companyName]);

  const handleSaveClick = () => {
    if (qrCodeDataUrl && onSaveAndDownload) {
      onSaveAndDownload(qrCodeDataUrl);
    }
  };

  const ShareMenu = isMobile ? ShareOptionsDrawer : ShareOptionsDialog;

  return (
    <div className="space-y-5 mt-2">
      <div className="flex justify-center">
        {isGenerating ? (
          <Skeleton className="w-[280px] h-[318px] rounded-lg" />
        ) : qrCodeDataUrl ? (
          <img src={qrCodeDataUrl} alt="Profile QR Code" className="w-full max-w-[280px] rounded-lg shadow-sm" />
        ) : (
          <div className="w-[280px] h-[318px] bg-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300">
            <span className="text-gray-400 text-sm">Failed to generate QR code</span>
          </div>
        )}
      </div>

      <p className="text-sm text-neutral-800 text-center">
        {translations?.shareDescription ||
          'If you share it with someone, they can scan and find your profile on TopTopTime'}
      </p>

      <div className="flex items-center gap-2">
        <div
          className="flex items-center justify-between flex-1 gap-3 px-3 py-2 text-sm bg-neutral-100 text-neutral-700 rounded-md cursor-pointer hover:bg-neutral-200 transition-colors overflow-hidden"
          onClick={() => copyButtonRef.current?.click()}
        >
          <div className="flex flex-col truncate max-w-[320px]">
            <label className="text-sm font-medium cursor-pointer">{translations?.profileUrl || 'Copy URL'}</label>
            <span className="text-sm text-neutral-500 truncate flex-1 min-w-0">{profileUrl}</span>
          </div>
          <CopyButton
            value={profileUrl}
            variant="icon"
            size="md"
            buttonRef={copyButtonRef}
            title={translations?.profileUrl || 'Copy URL'}
            copiedLabel={translations?.linkCopied || 'Copied!'}
            copiedTitle={translations?.linkCopied || 'Copied!'}
            aria-label={translations?.profileUrl || 'Copy URL'}
            stopPropagation
          />
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <Button
          variant="text"
          className="flex-1 gap-2"
          onClick={handleSaveClick}
          disabled={isGenerating || !qrCodeDataUrl}
        >
          <Download className="w-4 h-4" />
          {translations?.saveAndDownload || 'Save'}
        </Button>

        <Button className="flex-1 gap-2" onClick={() => setIsShareMenuOpen(true)}>
          <Share2 className="w-4 h-4" />
          {translations?.share || 'Share'}
        </Button>
      </div>

      <ShareMenu
        isOpen={isShareMenuOpen}
        onClose={() => setIsShareMenuOpen(false)}
        profileUrl={profileUrl}
        userName={userName}
        title={translations?.shareVia || 'Share via'}
        translations={{
          copyLink: translations?.copyLink,
          shareViaWhatsApp: translations?.shareViaWhatsApp,
          shareViaFacebook: translations?.shareViaFacebook,
          shareViaX: translations?.shareViaX,
          shareViaEmail: translations?.shareViaEmail,
          moreOptions: translations?.moreOptions,
          linkCopied: translations?.linkCopied
        }}
      />
    </div>
  );
}
